// eslint-disable-next-line
import React, { FC, ReactNode, useEffect, useState } from 'react';

import { QueryConfig, QueryResult, useQuery } from 'react-query';

import AsyncLoader from 'components/AsyncLoader';
import { DataGridPaginationProps } from 'components/DataGrid/DataGrid';
import { isNullOrUndefined } from 'utils/helpers';
import { mapErrorsToAsyncLoader } from 'core/errors';

type PaginationProps = { paginated?: boolean; page?: number; size?: number };

type Props = {
  queryKey: string;
  queryFn: Function;
  params?: { [key: string]: unknown };
  config?: QueryConfig<any>;
} & PaginationProps & {
    children: (
      state: QueryResult<any> & { pagination: DataGridPaginationProps }
    ) => ReactNode;
  };

const AsyncLoaderHelper: FC<Props> = ({
  queryKey,
  params,
  queryFn,
  page = 0,
  size = 10,
  paginated,
  children,
  config,
}) => {
  const [currentPage, setCurrentPage] = useState(page);
  const [currentSize, setCurrentSize] = useState(size);
  const mergeParams = {
    ...params,
    ...(paginated && { page: currentPage, size: currentSize }),
  };

  const state = useQuery(
    [queryKey, mergeParams],
    (key, queryParams) => queryFn({ params: queryParams }),
    config
  );

  const getPaginationProps = (data): DataGridPaginationProps => {
    return {
      pageSize: data.size,
      currentPage: data.number,
      totalCount: data.totalElements,
      onCurrentPageChange: setCurrentPage,
      onCurrentSizeChange: setCurrentSize,
      paginationMode: 'custom',
    };
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [currentSize]);

  const showChildren = ({ data }) => {
    if (isNullOrUndefined(data)) return null;

    const normalizedData = paginated ? { content: [], ...data } : data;
    const pagination = getPaginationProps(data);
    return children({
      ...state,
      data: normalizedData,
      pagination,
    });
  };

  return (
    <AsyncLoader
      loading={state.isLoading}
      error={!!state.error}
      onClickRetryButton={state.refetch}
      {...mapErrorsToAsyncLoader(state.error)}
    >
      {showChildren(state)}
    </AsyncLoader>
  );
};

export default AsyncLoaderHelper;
